import React, {
    useContext, useCallback, useState, useEffect, useRef
} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { ModalNextProvider } from '@jutro/components';
import {
    WizardPage,
    wizardProps,
    WizardPageTemplate,
    WizardPageTemplateWithTitle
} from 'gw-portals-wizard-react';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import { TranslatorContext } from '@jutro/locale';
import { Claim } from 'gw-capability-fnol-common-react';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { withRouter } from 'react-router-dom';
import { useValidation } from 'gw-portals-validation-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { messages as commonMessages } from 'gw-platform-translations';
import appConfig from 'app-config';
import metadata from './WhatPage.metadata.json5';
import LossCauseUtil from './LossCauseUtil';
import BlameCodeUtil from './BlameCodeUtil';
import messages from '../../FNOLCA.messages';
import wizardConfig from '../../config/fnol-ca-wizard-config.json5';

const { env } = appConfig;

function FNOLCAWhatPage(props) {
    const {
        wizardData: claimVM,
        updateWizardData,
        changeNextSteps,
        history,
        history: {
            location: { state = {} }
        },
        authHeader
    } = props;
    const { steps: wizardSteps } = wizardConfig;
    const [isLoading, setIsLoading] = useState(false);
    const { FNOLService } = useDependencies('FNOLService');
    const claimTypeImgPath = useRef(undefined);
    const [lossCauseTypeCodes, updatelossCauseTypeCodes] = useState(claimVM.lossCause.aspects.availableValues);
    const [pageData] = useState(state);
    const [policyLocations, updatePolicyLocations] = useState([]);
    const translator = useContext(TranslatorContext);

    const {
        onValidate,
        isComponentValid,
        initialValidation,
        registerInitialComponentValidation
    } = useValidation('FNOLCAWhatPage');

    const incidentPageHandling = useCallback(() => {
        const [, , ...remainingSteps] = wizardSteps;
        const detailPageIndex = 0;

        if (_.get(claimVM.value, 'lossCause') === '19'
            && !_.get(claimVM.value, 'isVehicleRecovered_Ext')) {
            remainingSteps.splice(detailPageIndex + 2, 1);
        }
        changeNextSteps(remainingSteps);
        return claimVM;
    }, [changeNextSteps, claimVM, wizardSteps]);

    useEffect(() => {
        incidentPageHandling();
        // It should call once when page is rendered
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const lossCauseDropdownValues = (selectedCode) => {
        const getLossCauseOptions = () => {
            if (selectedCode === 'collision') {
                return LossCauseUtil.getCollisionCodes();
            } if (selectedCode === 'theft') {
                return LossCauseUtil.getTheftCodes();
            }
            return LossCauseUtil.getOtherCodes();
        };
        const detailsCollisionTypeCodes = _.filter(
            claimVM.lossCause.aspects.availableValues,
            (typeCode) => {
                return getLossCauseOptions().indexOf(typeCode.code) !== -1;
            }
        ).map((collisionObj) => {
            return {
                code: collisionObj.code,
                name: translator({
                    id: collisionObj.name,
                    defaultMessage: collisionObj.name
                })
            };
        });
        return detailsCollisionTypeCodes;
    };

    const getIncidentTypeRadioButtonValue = () => {
        const lossCause = _.get(claimVM.value, 'lossCause');
        if (lossCause !== undefined) {
            return LossCauseUtil.getLossCauseCategory(lossCause);
        }
        return 'collision';
    };

    useEffect(() => {
        // TODO lino change for prod 
        const prefix = _.get(env, 'DEPLOYMENT_PATH', '').replace(/\/$/, '');
        claimTypeImgPath.current = `${prefix}/claimtype`;
        // claimTypeImgPath.current =  `/claimtype`;
        const isApplicableForPolicySummaryLoc = () => {
            if (claimVM.policy.policyNumber.value) {
                return (claimVM.policy.policyNumber.value.slice(0, 2).toLowerCase() === 'sm');
            }
            return false;
        };
        _.set(claimVM, 'isApplicableForPolicySummaryLoc_Ext.value', isApplicableForPolicySummaryLoc());

        if (isApplicableForPolicySummaryLoc) {
            const getPolicyLocationValues = () => {
                if (claimVM.isApplicableForPolicySummaryLoc_Ext.value
                    && claimVM.policy.policySummaryLocations.value) {
                    return claimVM.policy.policySummaryLocations.value.map((loc) => {
                        return {
                            code: loc.locationID,
                            name: loc.locationDescription
                        };
                    });
                }
                return [];
            };
            const policyLocationValues = getPolicyLocationValues();
            updatePolicyLocations(policyLocationValues);
        }
        if (_.get(claimVM.value, 'lossCause') === undefined) {
            updatelossCauseTypeCodes(lossCauseDropdownValues('collision'));
            const LossCause = _.find(claimVM.lossCause.aspects.availableValues, (cause) => {
                return cause.code === '1';
            });
            _.set(claimVM, 'lossType.value', _.first(claimVM.lossType.aspects.availableValues));
            _.set(claimVM, 'lossCause.value', LossCause);
            _.set(claimVM, 'blameCode_Ext.value', _.first(claimVM.blameCode_Ext.aspects.availableValues));
            _.set(claimVM, 'isVehicleRecovered_Ext.value', _.first(claimVM.isVehicleRecovered_Ext.aspects.availableValues));
            updateWizardData(claimVM);
        } else {
            const lossCodes = lossCauseDropdownValues(getIncidentTypeRadioButtonValue());
            updatelossCauseTypeCodes(lossCodes);
        }
        // It should call once when page is rendered
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLossCauseUpdate = (selectedLossCause) => {
        const lossCauseValues = lossCauseDropdownValues(selectedLossCause);
        updatelossCauseTypeCodes(lossCauseValues);

        let selectedCause = '10';
        if (selectedLossCause === 'collision') {
            selectedCause = '1';
        }
        if (selectedLossCause === 'theft') {
            selectedCause = '19';
        }
        const LossCause = _.find(claimVM.lossCause.aspects.availableValues, (cause) => {
            return cause.code === selectedCause;
        });
        _.set(claimVM, 'lossType.value', _.first(claimVM.lossType.aspects.availableValues));
        _.set(claimVM, 'lossCause.value', LossCause);
        _.set(claimVM, 'blameCode_Ext.value', _.first(claimVM.blameCode_Ext.aspects.availableValues));
        _.set(claimVM, 'isVehicleRecovered_Ext.value', _.first(claimVM.isVehicleRecovered_Ext.aspects.availableValues));
        updateWizardData(claimVM);
    };
    const OnChangelossCauseUpdate = (selectedCause) => {
        const LossCause = _.find(claimVM.lossCause.aspects.availableValues, (cause) => {
            return cause.code === selectedCause;
        });
        _.set(claimVM, 'lossCause.value', LossCause);
        _.set(claimVM, 'isVehicleRecovered_Ext.value', _.first(claimVM.isVehicleRecovered_Ext.aspects.availableValues));
        updateWizardData(claimVM);
    };

    const OnChangeblameCodeUpdate = (selectedBlameCode) => {
        const blameCode = _.find(claimVM.blameCode_Ext.aspects.availableValues, (cause) => {
            return cause.code === selectedBlameCode;
        });
        _.set(claimVM, 'blameCode_Ext.value', blameCode);
        updateWizardData(claimVM);
    };

    const OnChangeRecoveryUpdate = (selectedCause) => {
        _.set(claimVM, 'isVehicleRecovered_Ext.value', selectedCause);
        updateWizardData(claimVM);
    };

    const filteredBlameCodes = () => {
        if (claimVM.lossCause.value !== undefined) {
            return BlameCodeUtil.getFilteredBlameCodes(claimVM.lossCause.value.code,claimVM.blameCode_Ext.aspects.availableValues);
        }
        return claimVM.blameCode_Ext.aspects.availableValues;
    };

    const isClaimStatus = useCallback(() => {
        const { claimStatus } = pageData;
        return !_.isEmpty(claimStatus);
    }, [pageData]);

    useEffect(() => {
        registerInitialComponentValidation(isClaimStatus);
    }, [registerInitialComponentValidation, isClaimStatus]);

    const onNext = useCallback(() => {
        const policyNumber = _.get(claimVM.value, 'policy.policyNumber');
        const isClaimNumberAvailable = _.get(claimVM.value, 'claimNumber');
        if (isClaimNumberAvailable) {
            return incidentPageHandling();
        }
        setIsLoading(true);
        return FNOLService.getFNOLDetails(policyNumber, claimVM.value, authHeader)
            .then((response) => {
                claimVM.value = new Claim(response);
                return incidentPageHandling();
            })
            .catch(() => {
                ModalNextProvider.showAlert({
                    title: messages.caCreateDraftErrorTitle,
                    message: messages.caCreateDraftErrorMessage,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok
                }).catch(_.noop);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [FNOLService, authHeader, claimVM, incidentPageHandling]);

    const handlePolicySummaryLocationChange = (value) => {
        if ((claimVM.isApplicableForPolicySummaryLoc_Ext.value)
        && claimVM.policy.policySummaryLocations.value) {
            const selectedPolicyLocation = claimVM.policy.policySummaryLocations.value
                .filter((loc) => loc.locationID == value)[0];

            _.set(claimVM.value, 'policySummaryLoc', selectedPolicyLocation);
            updateWizardData(claimVM);
        }
    };


    const overrides = {
        incidentTypeRadioButton: {
            value: getIncidentTypeRadioButtonValue(),
            onValueChange: handleLossCauseUpdate,
            availableValues: [
                {
                    id:'collisionOption',
                    code: 'collision',
                    displayName: messages.caCollision,
                    iconSrc: `${claimTypeImgPath.current}/Collision.svg`,
                },
                {
                    id:'theftOption',
                    code: 'theft',
                    displayName: messages.caTheft,
                    iconSrc: `${claimTypeImgPath.current}/Theft.svg`,
                },
                {
                    id:'otherOption',
                    code: 'other',
                    displayName: messages.caOther,
                    iconSrc: `${claimTypeImgPath.current}/Other.svg`,
                }
            ]
        },
        policyLocationTitle:{
            visible: claimVM.isApplicableForPolicySummaryLoc_Ext.value
        },
        typeOfCollisionDropDown: {
            availableValues: lossCauseTypeCodes,
            onValueChange: OnChangelossCauseUpdate
        },

        blameCodeDropDown: {
            availableValues: filteredBlameCodes().map((blamecode) => {
                return {
                    code: blamecode.code,
                    name: translator({
                        id: blamecode.name,
                        defaultMessage: blamecode.name
                    })
                };
            }),
            onValueChange: OnChangeblameCodeUpdate
        },

        whatPageLoader: {
            loaded: !isLoading,
            loadingMessage: translator(messages.caCreateDraftClaim)
        },

        claimsCaTypeOfIncidentPage: {
            visible: !isLoading
        },

        isVehicleRecovered:{
            visible: claimVM.isVehicleRecovered_Ext.aspects.required,
            onValueChange: OnChangeRecoveryUpdate
        },

        policyLocationDropDown:{
            visible: claimVM.isApplicableForPolicySummaryLoc_Ext.value,
            availableValues: policyLocations,
            value: _.get(claimVM, 'policySummaryLoc.value'),
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            handlePolicySummaryLocationChange: handlePolicySummaryLocationChange
        }
    };

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(metadata.pageContent, claimVM, id, path, overrides);
        },
        [claimVM, overrides]
    );

    return (
        <WizardPage
            skipWhen={initialValidation}
            disableNext={!isComponentValid}
            onNext={onNext}
            nextButtonID={translator(messages.fnolWhatPageNextButton)}
            template={WizardPageTemplate}
            disablePrevious={true}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                resolveValue={readValue}
                overrideProps={overrides}
                onValidationChange={onValidate}
                onModelChange={updateWizardData}
                callbackMap={resolvers.resolveCallbackMap}
            />
        </WizardPage>
    );
}

FNOLCAWhatPage.propTypes = wizardProps;
FNOLCAWhatPage.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired
};
export default withRouter(withAuthenticationContext(FNOLCAWhatPage));
